import { useEffect, useRef, useState } from "react";
import { FormInputIcon } from "lucide-react";

import { AntiIdealCandidateProfiles } from "./AntiIdealCandidateProfiles";
import { DatesAndUrgency } from "./DatesAndUrgency";
import { IdealCandidateProfiles } from "./IdealCandidateProfiles";
import { InterviewProcess } from "./InterviewProcess";
import { JobCulture } from "./JobCulture";
import { JobGeneralInfo } from "./JobGeneralInfo";
import { JobIntakeAttachments } from "./JobIntakeAttachments";
import { JobScreeningQuestions } from "./JobScreeningQuestions";
import { JobStakeholders } from "./JobStakeholders";
import { JobTalentRecruitingHistory } from "./JobTalentRecruitingHistory";
import { IntakeCompletionDialog } from "@/components/companies/shared/IntakeCompletionDialog";
import { IntakeHeader } from "@/components/companies/shared/IntakeHeader";
import { IntakeProgress } from "@/components/companies/shared/IntakeProgress";
import { TableOfContentsButton } from "@/components/companies/shared/TableOfContentsButton";
import {
  Center,
  Column,
  EmptyState,
  ErrorText,
  LoadingIndicator,
  Row,
} from "@/components";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useCreateJobIntake } from "@/hooks/mutations/useCreateJobIntake";
import { useGetJobIntake } from "@/hooks/queries/useGetJobIntake";
import { useGetJobStakeholders } from "@/hooks/queries/useGetJobStakeholders";
import { jobIntakeTabToLabel } from "@/lib/constants";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { IntakeCompletion, JobIntakeTab, Progress } from "@/types";

/**
 * Map a JobIntakeTab enum value to its flat Progress on an IntakeCompletion.
 * Keeps the switch over tab kinds in one place.
 */
function getTabProgress(
  completion: IntakeCompletion,
  tab: JobIntakeTab,
): Progress {
  switch (tab) {
    case JobIntakeTab.Stakeholders:
      return completion.tabProgress.stakeholders.progress;
    case JobIntakeTab.GeneralInfo:
      return completion.tabProgress.generalInfo;
    case JobIntakeTab.TalentRecruitingHistory:
      return completion.tabProgress.talentRecruitingHistory;
    case JobIntakeTab.Culture:
      return completion.tabProgress.culture;
    case JobIntakeTab.IdealCandidateProfiles:
      return completion.tabProgress.idealCandidateProfiles.progress;
    case JobIntakeTab.AntiIdealCandidateProfiles:
      return completion.tabProgress.antiIdealCandidateProfiles.progress;
    case JobIntakeTab.InterviewProcess:
      return completion.tabProgress.interviewProcess.progress;
    case JobIntakeTab.DatesAndUrgency:
      return completion.tabProgress.datesAndUrgency;
    case JobIntakeTab.ScreeningQuestions:
      return completion.tabProgress.screeningQuestions;
  }
}

export const JobIntakeContainer = ({
  className,
  jobTitle,
}: {
  className?: string;
  jobTitle: string;
}) => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createJobIntake,
    isPending: isCreatePending,
    isError: isCreateError,
  } = useCreateJobIntake();

  // Read
  const {
    data,
    isPending: isJobIntakePending,
    isError: isJobIntakeError,
  } = useGetJobIntake();
  const jobIntake = data?.jobIntake ?? null;
  const intakeCompletion = data?.intakeCompletion ?? null;
  const { isPending: isJobStakeholdersPending } = useGetJobStakeholders();

  /* Misc Hooks */
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();

  /* React Hooks */
  // useRef
  const scrollRef = useRef<HTMLDivElement>(null);

  // useState
  const [activeTab, setActiveTab] = useState<JobIntakeTab>(
    JobIntakeTab.Stakeholders,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isCompletionDialogOpen, setIsCompletionDialogOpen] = useState(false);

  // useEffect
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeTab]);

  /* Handlers */
  const handleIntakeComplete = () => {
    setIsCompletionDialogOpen(true);
  };

  const handleCreateJobIntake = () => {
    createJobIntake(undefined, {
      onSuccess: () => {
        setActiveTab(JobIntakeTab.Stakeholders);
      },
    });
  };

  const render = () => {
    if (isJobIntakePending) {
      return <LoadingIndicator variant="medium" />;
    }

    if (isJobIntakeError) {
      return (
        <Center>
          <ErrorText errorMessage="An error occurred while loading the job intake. Please let the engineers know if they are not already on it!" />
        </Center>
      );
    }

    if (!jobIntake || !intakeCompletion) {
      return (
        <EmptyState
          description="Key information about this role and the client's requirements will go here."
          handlePrimaryClick={handleCreateJobIntake}
          Icon={FormInputIcon}
          IsLoading={isCreatePending}
          IsError={isCreateError}
          isErrorMessage="An error occurred while creating the job intake form. Please let the engineers know if they are not already on it!"
          primaryButtonName="Create Job Intake"
          title="Job Intake"
        />
      );
    }

    return (
      <Row className={cn("h-[calc(100vh-200px)]", className)}>
        <Column className="border-r border-slate-200 px-6 pb-0">
          <p className="mb-4 text-sm uppercase tracking-wider text-slate-600">
            Steps
          </p>
          <Column className="gap-1">
            {Object.values(JobIntakeTab).map((tab, index) => {
              const tp = getTabProgress(intakeCompletion, tab);
              return (
                <TableOfContentsButton
                  key={tab}
                  activeTab={activeTab}
                  index={index + 1}
                  isDataSettled={!isJobStakeholdersPending}
                  label={jobIntakeTabToLabel[tab]}
                  screenName={MIXPANEL_SCREENS.JOB}
                  progress={{
                    filled: tp.filled,
                    total: tp.total,
                    graceNote:
                      tab === JobIntakeTab.InterviewProcess
                        ? "At least one interview round is required on top of the expectations for Laborup tab."
                        : undefined,
                  }}
                  setActiveTab={setActiveTab}
                  tabValue={tab}
                />
              );
            })}
          </Column>
          <IntakeProgress
            filled={intakeCompletion.overall.filled}
            total={intakeCompletion.overall.total}
            isDataSettled={!isJobStakeholdersPending}
            onComplete={handleIntakeComplete}
          />
          <div className="mt-auto">
            <JobIntakeAttachments jobIntake={jobIntake} />
          </div>
        </Column>
        <div
          ref={scrollRef}
          className="flex flex-1 flex-col overflow-y-auto px-12 py-0"
        >
          <IntakeHeader
            activeTab={activeTab}
            isEditing={isEditing}
            screenName={MIXPANEL_SCREENS.JOB}
            setActiveTab={setActiveTab}
            setIsEditing={setIsEditing}
            tabs={Object.values(JobIntakeTab)}
            tabToLabel={jobIntakeTabToLabel}
          />
          <div className="max-w-3xl">
            {activeTab === JobIntakeTab.Stakeholders && (
              <JobStakeholders
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                progressByStakeholderId={
                  intakeCompletion.tabProgress.stakeholders.items
                }
              />
            )}
            {activeTab === JobIntakeTab.GeneralInfo && (
              <JobGeneralInfo isEditing={isEditing} jobIntake={jobIntake} />
            )}
            {activeTab === JobIntakeTab.TalentRecruitingHistory && (
              <JobTalentRecruitingHistory
                isEditing={isEditing}
                jobIntake={jobIntake}
              />
            )}
            {activeTab === JobIntakeTab.Culture && (
              <JobCulture isEditing={isEditing} jobIntake={jobIntake} />
            )}
            {activeTab === JobIntakeTab.IdealCandidateProfiles && (
              <IdealCandidateProfiles
                isEditing={isEditing}
                jobIntake={jobIntake}
                setIsEditing={setIsEditing}
                itemsProgress={
                  intakeCompletion.tabProgress.idealCandidateProfiles.items
                }
              />
            )}
            {activeTab === JobIntakeTab.AntiIdealCandidateProfiles && (
              <AntiIdealCandidateProfiles
                isEditing={isEditing}
                jobIntake={jobIntake}
                setIsEditing={setIsEditing}
                itemsProgress={
                  intakeCompletion.tabProgress.antiIdealCandidateProfiles.items
                }
              />
            )}
            {activeTab === JobIntakeTab.InterviewProcess && (
              <InterviewProcess
                isEditing={isEditing}
                jobIntake={jobIntake}
                setIsEditing={setIsEditing}
                laborupRoundProgress={
                  intakeCompletion.tabProgress.interviewProcess.laborupRound
                }
                roundsProgress={
                  intakeCompletion.tabProgress.interviewProcess.items
                }
              />
            )}
            {activeTab === JobIntakeTab.DatesAndUrgency && (
              <DatesAndUrgency isEditing={isEditing} jobIntake={jobIntake} />
            )}
            {activeTab === JobIntakeTab.ScreeningQuestions && (
              <JobScreeningQuestions
                isEditing={isEditing}
                jobIntake={jobIntake}
              />
            )}
          </div>
        </div>
        <IntakeCompletionDialog
          entityName={jobTitle}
          onOpenChange={setIsCompletionDialogOpen}
          open={isCompletionDialogOpen}
          shareUrl={`${window.location.origin}/companies/${currentCompanyId}/jobs/${currentJobId}?t=job-intake`}
          type="job"
        />
      </Row>
    );
  };

  return render();
};
